import { JSX, useEffect, useMemo } from 'react';
import { UseFormReturn, useFieldArray, useWatch } from 'react-hook-form';

import { Grid } from 'components/layout';
import { Typography } from 'components/typography';
import { useLoadContext } from 'hooks/useLoadContext';
import { LineItemSection } from 'pages/QuoteView/LoadBuilding/TurvoSectionForms/components/LineItemSection';
import { CostLineItem, NormalizedLoad } from 'types/Load';
import { getFieldAttribute } from 'types/LoadAttributes';

import { FuelSurchargeFields } from '../components/FuelSurchargeFields';
import { LoadBuildingTextInput } from '../components/LoadBuildingTextInput';
import { RateGrid, RateParty, RateType } from '../components/RateGrid';
import { PlsTripMiles } from './PlsTripMiles';
import {
  PLS_FUEL_SURCHARGE_TYPE_OPTIONS,
  PLS_RATE_TYPE_OPTIONS,
} from './constants';
import { usePlsAccessorials } from './hooks/usePlsReferenceCatalogs';
import { usePlsSpecsAccessorialCharges } from './hooks/usePlsSpecsAccessorialCharges';
import { PlsFormValues } from './types';
import { plsAccessorialOptions } from './utils';

interface RatesSectionProps {
  formMethods: UseFormReturn<PlsFormValues>;
  tmsID: number | undefined;
  isCreateMode: boolean;
}

// PLS takes the customer rate on create only. The Builder edits the customer rate. Load View
// shows that rate and the carrier side read-only, from the load's attributes.
export function RatesSection({
  formMethods,
  tmsID,
  isCreateMode,
}: RatesSectionProps): JSX.Element {
  // The shared rate components are typed on NormalizedLoad; PLS form values keep its rateData
  // as is, so the cast only widens the accessorials field they never touch.
  const loadFormMethods =
    formMethods as unknown as UseFormReturn<NormalizedLoad>;
  const { control, setValue } = loadFormMethods;
  const { fieldAttributes } = useLoadContext();
  const { accessorials } = usePlsAccessorials(tmsID);

  const {
    fields: customerLineItems,
    append,
    remove,
  } = useFieldArray({ control, name: 'rateData.customerLineItems' });
  const removeCharge = usePlsSpecsAccessorialCharges({
    formMethods,
    enabled: isCreateMode,
    append,
    remove,
  });
  const watchedCustomerLineItems = useWatch({
    control,
    name: 'rateData.customerLineItems',
  });
  const watchedRateType = useWatch({
    control,
    name: 'rateData.customerRateType',
  });
  // Create sends a load without a rate type as PLS's flat rate, so the card shows it as one.
  useEffect(() => {
    if (isCreateMode && !watchedRateType) {
      setValue('rateData.customerRateType', RateType.Flat, {
        shouldDirty: false,
      });
    }
  }, [isCreateMode, watchedRateType, setValue]);

  // PLS keeps weight on its items, so a CWT rate rates on their total.
  const watchedCommodities = useWatch({ control, name: 'commodities' });
  const totalWeightLbs = (watchedCommodities ?? []).reduce(
    (sum, commodity) => sum + (Number(commodity?.weightTotal) || 0),
    0
  );

  const chargeTypeOptions = useMemo(
    () => plsAccessorialOptions(accessorials),
    [accessorials]
  );
  const areLineItemsReadOnly = Boolean(
    getFieldAttribute(fieldAttributes, 'rateData.customerLineItems')?.isReadOnly
  );

  return (
    <Grid cols='4' gap='sm' className='mx-0 w-full' data-testid='pls-rates'>
      <Typography
        variant='h6'
        className='col-span-4 text-md text-neutral-800 font-semibold mb-1'
      >
        Customer
      </Typography>
      <RateGrid
        party={RateParty.Customer}
        formMethods={loadFormMethods}
        rateTypeOptions={PLS_RATE_TYPE_OPTIONS}
        totalWeightLbs={totalWeightLbs || undefined}
        // A per-mile rate rates on the trip's miles, which the rep can enter or correct before
        // create. Load View shows the miles PLS computed.
        unitsInput={
          isCreateMode && watchedRateType === RateType.Distance ? (
            <PlsTripMiles formMethods={loadFormMethods} tmsID={tmsID} />
          ) : undefined
        }
      />
      <FuelSurchargeFields
        formMethods={loadFormMethods}
        typeOptions={PLS_FUEL_SURCHARGE_TYPE_OPTIONS}
      />

      <div className='col-span-4' data-testid='pls-accessorial-charges'>
        <LineItemSection
          title='Accessorial Charges'
          formMethods={loadFormMethods}
          lineItemsFieldPath='rateData.customerLineItems'
          fields={customerLineItems}
          onAddCharge={() => append(newAccessorialCharge())}
          onRemoveCharge={removeCharge}
          watchedLineItems={watchedCustomerLineItems ?? []}
          chargeTypeOptions={chargeTypeOptions}
          addChargeDescription='Add accessorial charges like tarps or detention.'
          readOnly={areLineItemsReadOnly}
          hideRateQualifier
        />
      </div>

      {!isCreateMode && (
        <>
          <div className='col-span-4'>
            <LoadBuildingTextInput
              name='rateData.customerTotalCharge.val'
              label='Customer Total'
              inputType='number'
              options={{ valueAsNumber: true }}
              required={false}
            />
          </div>

          <hr className='col-span-4 my-1' />
          <Typography
            variant='h6'
            className='col-span-4 text-md text-neutral-800 font-semibold mb-1'
          >
            Carrier
          </Typography>
          <RateGrid
            party={RateParty.Carrier}
            formMethods={loadFormMethods}
            rateTypeOptions={PLS_RATE_TYPE_OPTIONS}
          />
        </>
      )}
    </Grid>
  );
}

// PLS prices every accessorial line flat (the backend sends FLAT_RATE), so a new charge needs no
// unit; an empty one also opens the card in edit mode.
function newAccessorialCharge(): CostLineItem {
  return {
    label: '',
    unitBasis: '',
    quantity: 1,
    ratePerUnitUSD: 0,
    totalChargeUSD: 0,
    note: '',
  };
}
